// OGSAvatar — the fused OGS inference super-node. Takes the rig animation in and
// emits geom_uv + color_uv straight to the terminal renderer, replacing the
// separate encoder + geom/appr decoders + plücker cluster with one node
// (`animation → ogs.avatar → renderer`). It COMPOSES the existing engine classes
// (two OGSDecoders via the shared factories + one OGSPluckerCamera); all heavy
// ONNX/plücker work lives in those — this node only wires them and caches `code`.
//
// The geom/appearance split is preserved so a camera-only orbit re-runs only
// plücker + appr (reusing the cached `code`), never geom. OGSModel drives
// runGeom (rig change) and runAppearance (camera change) through its mutex;
// evaluate() is the Node-contract full path for tests / any GraphEvaluator use.

import type * as THREE from "three";
import { OGSPluckerCamera } from "./OGSPluckerCamera.ts";
import { makeApprDecoder, makeGeomDecoder } from "./decoderFactories.ts";
import type { OGSDecoder } from "./OGSDecoder";
import type { DecoderSession } from "../DecoderSession";
import type {
  DirtySource,
  FrameInputs,
  Node,
  NodeContext,
  NodeTensor,
  PortSpec,
} from "../types";

export interface OGSAvatarOptions {
  id: string;
  rigInputPort?: string; // graph-facing input port (default 'rig'); mapped to geom's ORT 'rig_params'
  originScale?: number; // 1/worldScale for the plücker origin
  // scene.json world_offset — subtracted from the camera before originScale so
  // the plücker moment is computed in the splat's local frame (plucker.ts).
  worldOffset?: readonly number[];
  // scene.json world_rotation as a quaternion — inverted onto the plücker camera so
  // appr sees the training frame the mesh is rotated out of (render/worldRotation.ts).
  worldRotation?: THREE.Quaternion | null;
  // Runtime path: build the sub-nodes from bytes.
  providers?: string[];
  geomBytes?: Uint8Array;
  apprBytes?: Uint8Array;
  // Optional separate trunk (rig_params → code). Present for a multi-region
  // branch, absent for the FUSED single-region graph where geom takes the rig
  // directly and emits `code` itself. Purely additive: undefined reproduces the
  // original fused behaviour exactly.
  trunk?: Node;
  // Test path: inject the sub-nodes directly.
  geom?: Node;
  appr?: Node;
  plucker?: Node;
}

export class OGSAvatar implements Node {
  readonly id: string;
  readonly type = "ogsAvatar";
  readonly inputs: readonly PortSpec[];
  readonly outputs: readonly PortSpec[] = [{ name: "geom_uv" }, { name: "color_uv" }];
  readonly bindings = [];

  private readonly rigPort: string;
  private readonly trunk: Node | null;
  private readonly geom: Node;
  private readonly appr: Node;
  private readonly plucker: Node;
  private lastCode: NodeTensor | null = null;
  private lastGeom: NodeTensor | null = null;
  // Region-factored (V8+) bundles: the trunk's packed per-group rig latent,
  // which geom/appr slice for their baked mask-gated FiLM. Cached alongside
  // `code` and for the same reason — a camera-only orbit reuses both rather than
  // re-running the trunk. null for every pre-V8 bundle, where the trunk simply
  // has no such output and the decoders no such input.
  private lastZpack: NodeTensor | null = null;
  private reportedRegion = false;

  /** True once a trunk run has produced a `zpack`, i.e. the loaded bundle is
   *  region-factored. REPORTED from the asset, never configured. */
  get regionFactored(): boolean {
    return this.lastZpack !== null;
  }

  constructor(opts: OGSAvatarOptions) {
    this.id = opts.id;
    this.rigPort = opts.rigInputPort ?? "rig";
    this.inputs = [{ name: this.rigPort }];
    const providers = opts.providers ?? [];
    this.trunk = opts.trunk ?? null;
    this.geom = opts.geom ?? makeGeomDecoder({
      id: `${opts.id}.geom`,
      bytes: opts.geomBytes as Uint8Array,
      providers,
      // A separate trunk means the geom graph is the code→geom_uv slice.
      input: this.trunk ? "code" : "rig_params",
    });
    this.appr = opts.appr ?? makeApprDecoder({ id: `${opts.id}.appr`, bytes: opts.apprBytes as Uint8Array, providers });
    this.plucker = opts.plucker ?? new OGSPluckerCamera({
      id: `${opts.id}.plucker`,
      originScale: opts.originScale ?? 1,
      worldOffset: opts.worldOffset,
      worldRotation: opts.worldRotation,
    });
  }

  async init(ctx: NodeContext): Promise<void> {
    await Promise.all([
      ...(this.trunk ? [this.trunk.init(ctx)] : []),
      this.geom.init(ctx),
      this.appr.init(ctx),
      this.plucker.init(ctx),
    ]);
  }

  /** The plücker sub-node — exposed so the pipeline can read its resolved
   *  GPU/CPU mode and drive the __ISAAC_GPU_PARITY__ debug readback. */
  get pluckerNode(): Node {
    return this.plucker;
  }

  // Rig-dirty path: decode geometry, cache the `code` latent for appearance reuse.
  // With a trunk: rig → trunk → code → geom. Without one (the fused single-region
  // graph): rig → geom, which emits `code` alongside geom_uv.
  async runGeom(frame: FrameInputs, rigTensor: NodeTensor): Promise<{ geom_uv: NodeTensor }> {
    if (this.trunk) {
      const tOut = await this.trunk.evaluate(frame, { rig_params: rigTensor });
      const code = tOut.code;
      if (!code) throw new Error(`OGSAvatar "${this.id}": trunk produced no "code"`);
      this.lastCode = code;
      // Absent on a pre-V8 trunk. Passed through unconditionally: whether it is
      // CONSUMED is the geom/appr session's call (its own graph declares the
      // input or doesn't), so a composite branch inside a region bundle takes
      // `code` alone without this node needing to know which kind it is.
      this.lastZpack = tOut.zpack ?? null;
      if (this.lastZpack && !this.reportedRegion) {
        this.reportedRegion = true;
        console.info(
          `[ogs] ${this.id}: region-factored assets (trunk emits \`zpack\`) — geom/appr FiLM is live`,
        );
      }
      const gOut = await this.geom.evaluate(frame, this.geomFeeds(code));
      this.lastGeom = gOut.geom_uv;
      return { geom_uv: gOut.geom_uv };
    }
    const out = await this.geom.evaluate(frame, { rig_params: rigTensor });
    this.lastCode = out.code;
    this.lastGeom = out.geom_uv;
    return { geom_uv: out.geom_uv };
  }

  // Feed objects, reused across runs. Four pre-shaped records rather than one
  // mutated with `delete`: the KEY SET has to be exact (an absent optional input
  // and one present-but-undefined are the same to DecoderSession, but not to a
  // test asserting a pre-V8 run feeds exactly `code`), and `delete` on a
  // per-frame path deoptimises the object. DecoderSession reads the feeds
  // synchronously and never retains them, so reuse is safe.
  private readonly _geomFeeds: Record<string, NodeTensor> = { code: null as never };
  private readonly _geomFeedsZ: Record<string, NodeTensor> = { code: null as never, zpack: null as never };
  private readonly _apprFeeds: Record<string, NodeTensor> = { code: null as never, plucker: null as never };
  private readonly _apprFeedsZ: Record<string, NodeTensor> = { code: null as never, plucker: null as never, zpack: null as never };

  /** Geom feeds: `code`, plus `zpack` when the trunk produced one. A pre-V8 run
   *  feeds exactly `code`, as it did before region support existed. */
  private geomFeeds(code: NodeTensor): Record<string, NodeTensor> {
    if (!this.lastZpack) { this._geomFeeds.code = code; return this._geomFeeds; }
    this._geomFeedsZ.code = code;
    this._geomFeedsZ.zpack = this.lastZpack;
    return this._geomFeedsZ;
  }

  /** Appr feeds: `code` + `plucker`, plus `zpack` when the trunk produced one. */
  private apprFeeds(code: NodeTensor, plucker: NodeTensor): Record<string, NodeTensor> {
    if (!this.lastZpack) {
      this._apprFeeds.code = code; this._apprFeeds.plucker = plucker;
      return this._apprFeeds;
    }
    this._apprFeedsZ.code = code;
    this._apprFeedsZ.plucker = plucker;
    this._apprFeedsZ.zpack = this.lastZpack;
    return this._apprFeedsZ;
  }

  /** Every live DecoderSession behind this node (trunk/geom/appr), in run order.
   *  Used by the gpu-buffer DEMOTE path (setDownloadOutputs on every session)
   *  and the pipeline-level ORT profile dump. Empty for injected test stubs. */
  sessions(): DecoderSession[] {
    const out: DecoderSession[] = [];
    for (const n of [this.trunk, this.geom, this.appr]) {
      const s = n ? (n as unknown as Partial<OGSDecoder>).activeSession : null;
      if (s) out.push(s);
    }
    return out;
  }

  /** Most recent geom_uv (the frozen-appearance re-lift / dyno one-time decode). */
  get lastGeomUv(): NodeTensor | null {
    return this.lastGeom;
  }

  // Camera-dirty path: reuse the cached `code`; only plücker + appr re-run.
  async runAppearance(frame: FrameInputs): Promise<{ color_uv: NodeTensor }> {
    if (!this.lastCode) throw new Error(`OGSAvatar "${this.id}": runAppearance before runGeom`);
    const pOut = await this.plucker.evaluate(frame, {});
    const aOut = await this.appr.evaluate(frame, this.apprFeeds(this.lastCode, pOut.plucker));
    return { color_uv: aOut.color_uv };
  }

  // Node-contract full path (tests / any GraphEvaluator-driven use). OGSModel calls
  // runGeom/runAppearance directly so a camera orbit doesn't re-run geom.
  async evaluate(
    frame: FrameInputs,
    inbound: Readonly<Record<string, NodeTensor>>,
  ): Promise<Record<string, NodeTensor>> {
    const rig = inbound[this.rigPort];
    if (!rig) throw new Error(`OGSAvatar "${this.id}": missing input "${this.rigPort}"`);
    const { geom_uv } = await this.runGeom(frame, rig);
    const { color_uv } = await this.runAppearance(frame);
    return { geom_uv, color_uv };
  }

  dirtyOn(): readonly DirtySource[] {
    return ["rig", "camera"];
  }

  // --- decoder precision (the OGS Avatar node's fp16/fp32 dropdown) ---------
  // fp16 and fp32 share fp32 IO, so a swap only exchanges internal weights —
  // lift / plücker / rig2mesh / shared device are untouched. The first switch to
  // a precision builds + caches it; every switch after is a reference swap.
  // Trunk is excluded on purpose: no fp16 trunk asset ships.

  /** Swap both decoders' precision without a reload. Drops the cached `code`
   *  latent so the next runGeom recomputes it against the new decoders.
   *  CALLER MUST ensure no session.run() is in flight (OGSModel's mutex). */
  async setPrecision(preferFp16: boolean): Promise<void> {
    const swap = async (n: Node) => {
      const d = n as unknown as Partial<OGSDecoder>;
      if (typeof d.setPrecision === "function") await d.setPrecision(preferFp16);
    };
    await swap(this.geom);
    await swap(this.appr);
    this.lastCode = null;
    this.lastZpack = null;
  }

  /** Build + cache a precision WITHOUT activating it (background warm-up). */
  async prebuild(preferFp16: boolean): Promise<void> {
    const warm = async (n: Node) => {
      const d = n as unknown as Partial<OGSDecoder>;
      if (typeof d.prebuild === "function") await d.prebuild(preferFp16);
    };
    await warm(this.geom);
    await warm(this.appr);
  }

  /** Precision each decoder ACTUALLY built at (fp16 auto-downgrades to fp32 on
   *  an adapter without shader-f16). */
  get geomUsedFp16(): boolean {
    return !!(this.geom as unknown as Partial<OGSDecoder>).usedFp16;
  }
  get apprUsedFp16(): boolean {
    return !!(this.appr as unknown as Partial<OGSDecoder>).usedFp16;
  }

  dispose(): void {
    this.trunk?.dispose();
    this.geom.dispose();
    this.appr.dispose();
    this.plucker.dispose();
    this.lastCode = null;
    this.lastGeom = null;
    this.lastZpack = null;
  }
}
